/**
 * #472 — reads over the relay's Oscar blocker registry.
 *
 * Two consumers, one wire source (`/api/kalshi/oscar/blockers*`):
 *
 *   useOscarKillPreview — the ticket's pre-fire warning. Asks the relay which
 *   friendly blockers the previewed take would cross (the SAME query the
 *   sequence route uses to derive kill legs), so the click preview shows
 *   exactly what would fire — "KILL <name>'s blocker @ x¢". Debounced; a
 *   response for a superseded (scope, ticker, side, price, quantity) is dropped.
 *
 *   useOscarOvertakenNotices — the "your blocker was overtaken" ghost rows.
 *   A killed blocker simply disappears from Open Orders, so this polls the
 *   trader's own registry rows and surfaces recent `overtaken` transitions
 *   with the relay-resolved self/teammate ownership class.
 *
 * Ownership class is RELAY-supplied throughout — the frontend never infers
 * another trader's identity from local state (oscarResultPresentation.ts contract).
 * Every fetch failure lands as "no data", never a throw.
 */
import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/stores/authStore';

export interface OscarKillPreviewBlocker {
  own: boolean;
  takePriceCents: number;
  blockerPriceCents: number;
  count: number;
}

/**
 * #756 — same-account resting orders the take would cross that are NOT in the
 * blocker registry, so Kalshi self-trade prevention, not Oscar, decides their
 * fate. A count and nothing else: the relay refuses to say whose they are.
 *
 * `unknown` is a first-class answer, never a dressed-up zero — the relay
 * reports it whenever the account, the completeness, or the rows themselves
 * could not be proved.
 */
export type OscarOrdinaryExposure =
  | { state: 'known'; orders: number; contracts: number; asOf: number }
  | {
      state: 'unknown';
      reason: 'account_authority' | 'read_failed' | 'read_incomplete';
      asOf: number | null;
    };

export interface OscarKillPreview {
  killMode: 'off' | 'shadow' | 'on';
  blockers: OscarKillPreviewBlocker[];
  ordinaryExposure: OscarOrdinaryExposure;
}

const UNKNOWN_REASONS = ['account_authority', 'read_failed', 'read_incomplete'] as const;

/**
 * A relay that does not send the field at all is treated as unknown, not as
 * zero: an older deployment has not proved an empty path, it simply never
 * looked.
 */
function parseOrdinaryExposure(value: unknown): OscarOrdinaryExposure | null {
  if (value === undefined) return { state: 'unknown', reason: 'read_failed', asOf: null };
  if (value == null || typeof value !== 'object') return null;
  const c = value as Record<string, unknown>;
  if (c.state === 'known') {
    if (!Number.isFinite(c.orders) || !Number.isFinite(c.contracts)) return null;
    if (!Number.isFinite(c.asOf)) return null;
    return {
      state: 'known',
      orders: c.orders as number,
      contracts: c.contracts as number,
      asOf: c.asOf as number,
    };
  }
  if (c.state === 'unknown') {
    if (!UNKNOWN_REASONS.includes(c.reason as (typeof UNKNOWN_REASONS)[number])) return null;
    if (c.asOf != null && !Number.isFinite(c.asOf)) return null;
    return {
      state: 'unknown',
      reason: c.reason as (typeof UNKNOWN_REASONS)[number],
      asOf: (c.asOf as number | null) ?? null,
    };
  }
  return null;
}

/**
 * Validate AND project. The returned object is rebuilt from the exact fields
 * this module renders, so a relay that grows a field — an owner, an order id,
 * a credential — cannot carry it onto a component prop and out to the DOM.
 * Ownership stays a boolean class; nothing else about another trader is
 * representable here.
 */
function parseOscarKillPreview(value: unknown): OscarKillPreview | null {
  if (value == null || typeof value !== 'object') return null;
  const candidate = value as Partial<OscarKillPreview>;
  if (!['off', 'shadow', 'on'].includes(candidate.killMode ?? '')) return null;
  if (!Array.isArray(candidate.blockers)) return null;
  if (
    !candidate.blockers.every(
      (blocker) =>
        blocker != null &&
        typeof blocker === 'object' &&
        typeof blocker.own === 'boolean' &&
        Number.isFinite(blocker.takePriceCents) &&
        Number.isFinite(blocker.blockerPriceCents) &&
        Number.isFinite(blocker.count),
    )
  ) {
    return null;
  }
  const ordinaryExposure = parseOrdinaryExposure(
    (value as Record<string, unknown>).ordinaryExposure,
  );
  if (ordinaryExposure == null) return null;
  return {
    killMode: candidate.killMode as OscarKillPreview['killMode'],
    blockers: candidate.blockers.map((blocker) => ({
      own: blocker.own,
      takePriceCents: blocker.takePriceCents,
      blockerPriceCents: blocker.blockerPriceCents,
      count: blocker.count,
    })),
    ordinaryExposure,
  };
}

interface KeyedValue<T> {
  key: string;
  value: T;
}

const PREVIEW_DEBOUNCE_MS = 400;

/**
 * How often a rendered preview re-asks while the ticket sits open.
 *
 * An Overtake is often set up and then WAITED on — the trader parks a level and
 * pounces when the book comes to them — so a count fetched once at setup is the
 * one most likely to be read at the moment it is least true. Comfortably under
 * the staleness threshold, so a working refresh keeps the answer fresh rather
 * than letting it age into a caveat.
 *
 * Bounded by what it costs: org scope is served from the poller's snapshot and
 * adds no venue traffic at all, and a personal-key trader is polling their OWN
 * Kalshi account, which nothing else polls — not the shared org bucket the take
 * spends from. It runs only while the tab is visible and the ticket is in
 * Overtake with valid inputs.
 */
const PREVIEW_REFRESH_MS = 10_000;

export interface OscarKillPreviewResult {
  /** Last answer for the current inputs; null before one arrives, or when the
   *  FIRST fetch for these inputs failed (fail-closed, as before). */
  preview: OscarKillPreview | null;
  /** Browser monotonic clock when `preview` arrived. Deliberately the receipt time and
   *  not the relay's `asOf`: those are different clocks, and subtracting across
   *  them is the comparison docs/clock-domain-safety.md warns about. Receipt elapsed excludes upstream cache and transport age; it is not
   *  the source snapshot age. */
  receivedAtMs: number | null;
  /** A refresh has failed since `receivedAtMs`, so the answer is no longer
   *  being kept current. The last good value is still returned — the ticket
   *  downgrades what it CLAIMS rather than blanking the row. */
  refreshFailing: boolean;
  /** Browser clock at the last attempt, landed or not. It is the reference the
   *  ticket ages against before its own clock has ticked: sampling `performance.now()`
   *  during render is forbidden, so the one impure read lives here, where the
   *  attempt actually happened. */
  checkedAtMs: number | null;
}

const NO_PREVIEW: OscarKillPreviewResult = {
  preview: null,
  receivedAtMs: null,
  refreshFailing: false,
  checkedAtMs: null,
};

export function useOscarKillPreview(args: {
  enabled: boolean;
  /** Local authenticated user/org identity only. Never sent to the relay. */
  scopeKey: string | null;
  ticker: string | null;
  side: 'yes' | 'no' | null;
  priceCents: number | null;
  quantity: number | null;
}): OscarKillPreviewResult {
  const { enabled, scopeKey, ticker, side, priceCents, quantity } = args;
  const previewKey =
    enabled &&
    scopeKey &&
    ticker &&
    side &&
    priceCents != null &&
    Number.isInteger(priceCents) &&
    priceCents >= 1 &&
    priceCents <= 98 &&
    quantity != null &&
    Number.isFinite(quantity) &&
    quantity > 0
      ? JSON.stringify([scopeKey, ticker, side, priceCents, quantity])
      : null;
  const [previewState, setPreviewState] = useState<KeyedValue<OscarKillPreviewResult>>({
    key: '',
    value: NO_PREVIEW,
  });
  const generation = useRef(0);

  useEffect(() => {
    const gen = ++generation.current;
    if (previewKey == null || !ticker || !side || priceCents == null) return;

    // A refresh keeps the last good answer and records that it is no longer
    // current; only the FIRST fetch for these inputs fails closed to null.
    const fail = () => {
      if (generation.current !== gen) return;
      const checkedAtMs = performance.now();
      setPreviewState((prev) =>
        prev.key === previewKey && prev.value.preview != null
          ? { key: previewKey, value: { ...prev.value, refreshFailing: true, checkedAtMs } }
          : { key: previewKey, value: { ...NO_PREVIEW, checkedAtMs } },
      );
    };

    let active: AbortController | null = null;
    const load = async () => {
      if (active || generation.current !== gen) return;
      const controller = new AbortController();
      active = controller;
      const deadline = setTimeout(() => {
        controller.abort();
        fail();
      }, 5_000);
      try {
        const qs = new URLSearchParams({ ticker, side, priceCents: String(priceCents) });
        const res = await apiFetch(`/api/kalshi/oscar/blockers/at-level?${qs}`, { signal: controller.signal });
        if (controller.signal.aborted) return;
        if (!res.ok) return fail();
        const body = parseOscarKillPreview(await res.json());
        if (body == null) return fail();
        if (controller.signal.aborted || generation.current !== gen) return;
        const landedAtMs = performance.now();
        setPreviewState({
          key: previewKey,
          value: {
            preview: body,
            receivedAtMs: landedAtMs,
            refreshFailing: false,
            checkedAtMs: landedAtMs,
          },
        });
      } catch {
        fail();
      } finally {
        clearTimeout(deadline);
        if (active === controller) active = null;
      }
    };

    let refresh: ReturnType<typeof setInterval> | undefined;
    const startRefresh = () => {
      if (refresh != null) return;
      refresh = setInterval(() => void load(), PREVIEW_REFRESH_MS);
    };
    const stopRefresh = () => {
      if (refresh != null) clearInterval(refresh);
      refresh = undefined;
    };
    // A hidden tab is not being traded from; stop asking, and catch up the
    // moment it comes back rather than waiting out the interval.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void load();
        startRefresh();
      } else {
        stopRefresh();
      }
    };

    const timer = setTimeout(() => {
      void load();
      if (document.visibilityState === 'visible') startRefresh();
      document.addEventListener('visibilitychange', onVisibility);
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      ++generation.current;
      active?.abort();
      clearTimeout(timer);
      stopRefresh();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [previewKey, ticker, side, priceCents]);

  return previewKey != null && previewState.key === previewKey ? previewState.value : NO_PREVIEW;
}

export interface OscarOvertakenNotice {
  kalshiOrderId: string;
  ticker: string;
  takeSide: 'yes' | 'no';
  takePriceCents: number;
  blockerSide: 'yes' | 'no';
  blockerPriceCents: number;
  count: number;
  killedByClass: 'self' | 'teammate' | null;
  updatedAt: string;
}

interface RegistryBlockerWire extends OscarOvertakenNotice {
  status: string;
}

const NOTICE_POLL_MS = 15_000;
const NOTICE_WINDOW_MS = 15 * 60 * 1_000;

/**
 * Recent `overtaken` registry rows for tickers under any of the given
 * prefixes (a game's Kalshi event prefixes). Empty array while disabled,
 * loading, or when there is nothing to show.
 */
export function useOscarOvertakenNotices(args: {
  enabled: boolean;
  tickerPrefixes: string[];
}): OscarOvertakenNotice[] {
  const { enabled, tickerPrefixes } = args;
  const [noticeState, setNoticeState] = useState<KeyedValue<OscarOvertakenNotice[]>>({
    key: '',
    value: [],
  });
  // Join for the effect key so a stable set of prefixes doesn't re-poll.
  const prefixKey = tickerPrefixes.join('|');

  useEffect(() => {
    if (!enabled || prefixKey === '') return;
    let disposed = false;
    const prefixes = prefixKey.split('|');
    const load = async () => {
      try {
        const res = await apiFetch('/api/kalshi/oscar/blockers');
        if (!res.ok) return;
        const body = (await res.json()) as { blockers: RegistryBlockerWire[] };
        if (disposed) return;
        const cutoff = Date.now() - NOTICE_WINDOW_MS;
        setNoticeState({
          key: prefixKey,
          value: body.blockers.filter(
            (b) =>
              b.status === 'overtaken' &&
              new Date(b.updatedAt).getTime() >= cutoff &&
              prefixes.some((p) => b.ticker.startsWith(p)),
          ),
        });
      } catch {
        // keep last known notices; next poll retries
      }
    };
    void load();
    const timer = setInterval(() => void load(), NOTICE_POLL_MS);
    return () => {
      disposed = true;
      clearInterval(timer);
    };
  }, [enabled, prefixKey]);

  return enabled && prefixKey !== '' && noticeState.key === prefixKey ? noticeState.value : [];
}

/** `KXMLBGAME-26JUL30NYYBAL-NYY` → `KXMLBGAME-26JUL30NYYBAL-` — the event
 *  prefix every market of the same game shares. Null for null/blank input. */
export function kalshiEventPrefix(marketTicker: string | null | undefined): string | null {
  if (!marketTicker) return null;
  const lastDash = marketTicker.lastIndexOf('-');
  return lastDash > 0 ? marketTicker.slice(0, lastDash + 1) : null;
}
